import React, { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Card, List, Button, message } from "antd";
import { useAuth0 } from "@auth0/auth0-react";
import { useAppDispatch, useAppSelector } from "../store/hooks";
import { fetchSettings, saveCategories, MAX_CATEGORIES, type Category } from "../store/settingsSlice";
import { colors } from "../styles/colors";
import { CategoryDrawer } from "../components/CategoryDrawer";
import PageHeader from "../components/PageHeader";
import { AccountPrivacyPanel } from "../components/AccountPrivacyPanel";
import { MembershipPanel } from "../components/MembershipPanel";
import { useIsPremium } from "../hooks/useIsPremium";
import { FREE_PLAN_LIMITS, FREE_DEFAULT_CATEGORIES } from "../config/upgrade";
import { UpgradeModal } from "../components/upgrade";
import { request } from "../api/client";
import { API_URLS } from "../utils/urls";

export default function Settings() {
  const dispatch = useAppDispatch();
  const { getAccessTokenSilently } = useAuth0();
  const { categories, loading, saving } = useAppSelector((state) => state.settings);
  const getToken = () => getAccessTokenSilently({ cacheMode: "off" });

  const isPremium = useIsPremium();
  const categoryLimit = isPremium ? MAX_CATEGORIES : FREE_PLAN_LIMITS.categories;
  const [showUpgradeGate, setShowUpgradeGate] = useState(false);

  // Notification preferences
  const [notifPrefs, setNotifPrefs] = useState({ marketingEmails: true, taskReminderEmails: true });
  const [notifLoading, setNotifLoading] = useState(true);
  const [notifSaving, setNotifSaving] = useState(false);
  const notifRef = useRef<HTMLDivElement>(null);
  const [searchParams] = useSearchParams();

  const [drawer, setDrawer] = useState<{ open: boolean; mode: "add" | "edit"; name: string; color: string }>({
    open: false, mode: "add", name: "", color: "",
  });

  useEffect(() => {
    if (categories.length === 0 && !loading) dispatch(fetchSettings(getToken));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, categories.length, loading]);

  useEffect(() => {
    request(API_URLS.settingsNotificationPrefs, { auth: true, getAccessToken: getToken })
      .then((data) => { if (data?.notificationPrefs) setNotifPrefs(data.notificationPrefs); })
      .catch(() => {})
      .finally(() => setNotifLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (searchParams.get("tab") === "notifications" && notifRef.current) {
      setTimeout(() => notifRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 300);
    }
  }, [searchParams, notifLoading]);

  const handleNotifToggle = async (key: "marketingEmails" | "taskReminderEmails") => {
    const updated = { ...notifPrefs, [key]: !notifPrefs[key] };
    setNotifPrefs(updated);
    setNotifSaving(true);
    try {
      await request(API_URLS.settingsNotificationPrefs, {
        method: "PUT",
        auth: true,
        getAccessToken: getToken,
        body: updated,
      });
      message.success("Notification preferences saved");
    } catch {
      setNotifPrefs(notifPrefs);
      message.error("Failed to save preferences");
    } finally {
      setNotifSaving(false);
    }
  };

  const handleRemove = (cat: Category) => {
    const next = categories.filter((c) => c.name !== cat.name);
    dispatch(saveCategories({ categories: next, getAccessToken: getToken })).then((action) => {
      if (saveCategories.fulfilled.match(action)) {
        message.success("Category removed");
      } else {
        message.error("Failed to remove category");
      }
    });
  };

  return (
    <div className="space-y-4">
      <PageHeader title="Settings" subtitle="Manage your account preferences and dream categories" />
      <AccountPrivacyPanel />

      <MembershipPanel onUpgrade={() => setShowUpgradeGate(true)} />

      <Card
        title="Dream Categories"
        loading={loading}
        extra={
          <button
            type="button"
            disabled={categories.length >= categoryLimit}
            onClick={() => {
              if (!isPremium && categories.length >= FREE_PLAN_LIMITS.categories) {
                setShowUpgradeGate(true);
                return;
              }
              setDrawer({ open: true, mode: "add", name: "", color: "" });
            }}
            className="flex items-center gap-1.5 rounded-xl px-3 py-1.5 text-[12px] font-semibold transition-colors"
            style={{
              background: categories.length >= categoryLimit ? "transparent" : "rgba(0,136,255,0.06)",
              color: categories.length >= categoryLimit ? "#CBD5E1" : colors.brandPrimary,
              border: `1.5px dashed ${categories.length >= categoryLimit ? "#E2E8F0" : `${colors.brandPrimary}60`}`,
              cursor: categories.length >= categoryLimit ? "not-allowed" : "pointer",
              lineHeight: "1.25rem",
              boxSizing: "border-box",
            }}
          >
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
              <line x1="6" y1="1" x2="6" y2="11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
              <line x1="1" y1="6" x2="11" y2="6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
            <span className="hidden sm:inline">{categories.length >= categoryLimit ? (isPremium ? `Max ${MAX_CATEGORIES} categories` : `${FREE_PLAN_LIMITS.categories} max (free)`) : "Add category"}</span>
            <span className="sm:hidden">{categories.length >= categoryLimit ? "Max" : "Add"}</span>
          </button>
        }
      >
        <p className="mb-4 text-sm text-gray-500">
          Each category has a colour automatically applied to dreams in that category.{" "}
          {!isPremium && (
            <span className="text-amber-600">Free plan: {FREE_PLAN_LIMITS.categories} categories max.{" "}
              <button type="button" onClick={() => setShowUpgradeGate(true)} className="font-semibold underline" style={{ color: colors.brandPrimary, background: "none", border: "none", cursor: "pointer", padding: 0 }}>Upgrade for unlimited.</button>
            </span>
          )}
          {isPremium && categories.length >= MAX_CATEGORIES && (
            <span className="ml-1 text-amber-600">Maximum {MAX_CATEGORIES} categories reached.</span>
          )}
        </p>

        <List
          dataSource={categories}
          locale={{ emptyText: "No categories yet" }}
          renderItem={(cat, index) => {
            const isLocked = !isPremium && index >= FREE_PLAN_LIMITS.categories;
            return (
              <List.Item
                style={isLocked ? { opacity: 0.55 } : undefined}
                actions={isLocked ? [
                  <button
                    key="locked"
                    type="button"
                    onClick={() => setShowUpgradeGate(true)}
                    className="flex items-center gap-1.5 rounded-lg px-2.5 py-1 text-[11px] font-semibold"
                    style={{ background: "rgba(0,136,255,0.08)", color: colors.brandPrimary, border: `1px solid ${colors.brandPrimary}30`, cursor: "pointer" }}
                  >
                    <svg width="11" height="11" viewBox="0 0 11 11" fill="none">
                      <rect x="1.5" y="4.5" width="8" height="5.5" rx="1.2" stroke="currentColor" strokeWidth="1.2"/>
                      <path d="M3.5 4.5V3a2 2 0 1 1 4 0v1.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
                    </svg>
                    Premium
                  </button>,
                ] : [
                  <Button
                    key="edit"
                    size="small"
                    onClick={() => setDrawer({ open: true, mode: "edit", name: cat.name, color: cat.color })}
                  >
                    Edit
                  </Button>,
                  <Button
                    key="del"
                    danger
                    size="small"
                    onClick={() => handleRemove(cat)}
                    disabled={saving}
                  >
                    Remove
                  </Button>,
                ]}
              >
                <div className="flex items-center gap-3">
                  <span
                    className="h-5 w-5 flex-shrink-0 rounded-full"
                    style={{ background: cat.color, filter: isLocked ? "grayscale(0.4)" : undefined }}
                  />
                  <span className="text-sm font-medium text-slate-800">{cat.name}</span>
                  {isLocked && (
                    <span className="flex items-center gap-1 rounded-lg px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide" style={{ background: "rgba(0,136,255,0.08)", color: colors.brandPrimary }}>
                      <svg width="9" height="9" viewBox="0 0 9 9" fill="none">
                        <rect x="1" y="3.5" width="7" height="4.5" rx="1" stroke="currentColor" strokeWidth="1.1"/>
                        <path d="M2.5 3.5V2.5a2 2 0 1 1 4 0v1" stroke="currentColor" strokeWidth="1.1" strokeLinecap="round"/>
                      </svg>
                      Locked
                    </span>
                  )}
                </div>
              </List.Item>
            );
          }}
        />
      </Card>

      {/* Notification Preferences card */}
      <div ref={notifRef}>
        <Card
          title="Email Notifications"
          loading={notifLoading}
          style={searchParams.get("tab") === "notifications" ? { boxShadow: `0 0 0 2px ${colors.brandPrimary}40` } : undefined}
        >
          <p className="mb-5 text-sm text-slate-500">
            Control which emails DreamUp sends to you. Turning off an option will stop those emails immediately.
          </p>
          <div className="flex flex-col gap-4">
            {([
              {
                key: "marketingEmails" as const,
                label: "Marketing emails",
                description: "Subscription confirmations, plan changes, and product announcements.",
              },
              {
                key: "taskReminderEmails" as const,
                label: "Task reminder emails",
                description: "Upcoming milestone reminders and goal deadline alerts.",
              },
            ]).map(({ key, label, description }) => (
              <div key={key} className="flex items-start justify-between gap-4 rounded-xl px-4 py-3" style={{ background: "#F8FAFC", border: "1px solid #E2E8F0" }}>
                <div className="min-w-0 flex-1">
                  <p className="m-0 text-sm font-semibold text-slate-800">{label}</p>
                  <p className="m-0 mt-0.5 text-[12px] text-slate-500">{description}</p>
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-checked={notifPrefs[key]}
                  disabled={notifSaving}
                  onClick={() => handleNotifToggle(key)}
                  className="relative mt-0.5 flex-shrink-0 rounded-full transition-colors"
                  style={{
                    width: 40,
                    height: 22,
                    background: notifPrefs[key] ? colors.brandPrimary : "#CBD5E1",
                    border: "none",
                    cursor: notifSaving ? "not-allowed" : "pointer",
                    opacity: notifSaving ? 0.6 : 1,
                    padding: 0,
                  }}
                >
                  <span
                    style={{
                      position: "absolute",
                      top: 3,
                      left: notifPrefs[key] ? 21 : 3,
                      width: 16,
                      height: 16,
                      borderRadius: "50%",
                      background: "#fff",
                      transition: "left 0.15s",
                      boxShadow: "0 1px 3px rgba(0,0,0,0.18)",
                    }}
                  />
                </button>
              </div>
            ))}
          </div>
        </Card>
      </div>

      <CategoryDrawer
        open={drawer.open}
        mode={drawer.mode}
        initialName={drawer.name}
        initialColor={drawer.color || undefined}
        onClose={() => setDrawer((p) => ({ ...p, open: false }))}
      />
      <UpgradeModal
        open={showUpgradeGate}
        trigger="category_limit"
        onClose={() => setShowUpgradeGate(false)}
      />
    </div>
  );
}
